/**
 * 订单详情抽屉 - 状态徽章行区块
 *
 * 展示订单状态、支付状态、订单分类徽章，收款/活动/时间更新成功提示，
 * 订单抵消徽章与来源订单跳转标签。受控组件，不持有业务状态。
 */
import type { OrderItem } from '../../../../../types';
import { isReviewSettled } from '../utils';

interface Props {
  order: OrderItem;
  statusStyle: { bg: string; text: string; dot: string };
  computedPayStatus: string;
  payStyle: { bg: string; text: string; icon: string };
  /** 0 元占位单没有收款义务，付款徽章整块不下发（`hasNoPaymentObligation`） */
  showPaymentBadge: boolean;
  /**
   * 审核态与审核意见走调用方的本地快照（不是 `order` 上那份旧投影）：
   * 刚点完通过/拒绝时，抽屉手上的订单快照还没刷新，徽标必须立刻反映这一下。
   */
  reviewStatus: string | null;
  reviewStyle: { bg: string; text: string; icon: string; label: string } | null;
  reviewComment: string | null;
  paySuccess: boolean;
  activitySuccess: boolean;
  scheduleSuccess: boolean;
  localPayMethod: string | null;
  isCustomerOfOrder: boolean;
  sourceOrder: OrderItem['sourceOrder'];
  sourceStaffRole: string | null;
  onNavigateToOrder?: (orderId: string) => void;
}

export default function BadgesSection({
  order,
  statusStyle,
  computedPayStatus,
  payStyle,
  showPaymentBadge,
  reviewStatus,
  reviewStyle,
  reviewComment,
  paySuccess,
  activitySuccess,
  scheduleSuccess,
  localPayMethod,
  isCustomerOfOrder,
  sourceOrder,
  sourceStaffRole,
  onNavigateToOrder,
}: Props) {
  return (
    <>
      {/* Badges row */}
      <div className="flex items-center gap-2 flex-wrap px-5 py-3 border-b border-gray-100 shrink-0 bg-gray-50">
        <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full font-medium ${statusStyle.bg} ${statusStyle.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
          {order.orderStatus ?? '未知状态'}
        </span>
        {showPaymentBadge && (
          <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border font-medium ${payStyle.bg} ${payStyle.text}`}>
            <div className="w-3.5 h-3.5 flex items-center justify-center"><i className={`${payStyle.icon} text-xs`} /></div>
            {computedPayStatus}
          </span>
        )}
        {order.orderCategory && (() => {
          const isExpense = order.orderCategory.includes('支出');
          return (
            <span className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full font-medium ${isExpense ? 'bg-rose-50 text-rose-500' : 'bg-emerald-50 text-emerald-600'}`}>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={isExpense ? 'ri-subtract-line text-xs' : 'ri-add-line text-xs'} />
              </div>
              {order.orderCategory}
            </span>
          );
        })()}
        {/*
          审核终态（已通过/已拒绝）：整张审核面板不再渲染，结果就收敛成这一枚徽标；
          「待审核」还在等人动作，面板由调用方继续渲染（按钮与违约风险提示都在里面）。
          拒绝理由是下单方唯一能看见的「对方为什么不同意」，所以意见跟着徽标留一行，超长折进 hover。
        */}
        {isReviewSettled(reviewStatus) && reviewStyle && (
          <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border font-medium ${reviewStyle.bg} ${reviewStyle.text}`}>
            <div className="w-3.5 h-3.5 flex items-center justify-center"><i className={`${reviewStyle.icon} text-xs`} /></div>
            审核{reviewStyle.label}
          </span>
        )}
        {isReviewSettled(reviewStatus) && reviewComment && (
          <p className="w-full text-xs text-gray-500 leading-relaxed line-clamp-2" title={`审核意见：${reviewComment}`}>审核意见：{reviewComment}</p>
        )}
        {paySuccess && <span className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-teal-50 text-teal-600 rounded-full border border-teal-200 animate-pulse"><i className="ri-check-line text-xs" />收款已更新</span>}
        {activitySuccess && <span className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-teal-50 text-teal-600 rounded-full border border-teal-200 animate-pulse"><i className="ri-check-line text-xs" />活动信息已更新</span>}
        {scheduleSuccess && <span className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-teal-50 text-teal-600 rounded-full border border-teal-200 animate-pulse"><i className="ri-check-line text-xs" />时间安排已更新</span>}
        {localPayMethod === '订单抵消' && (
          <span className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-teal-600 text-white rounded-full font-medium shadow-sm">
            <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-exchange-dollar-line text-xs" /></div>
            订单抵消结算
          </span>
        )}
        {localPayMethod?.startsWith('订单抵消—') && (
          <span className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-teal-600 text-white rounded-full font-medium shadow-sm">
            <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-exchange-dollar-line text-xs" /></div>
            {localPayMethod}
          </span>
        )}
        {/* 来源订单标签（仅订单用户方可见；客户方视角隐藏） */}
        {!isCustomerOfOrder && sourceOrder && (
          <button
            type="button"
            onClick={() => { if (onNavigateToOrder) { onNavigateToOrder(sourceOrder.id); } }}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full font-medium hover:bg-indigo-100 cursor-pointer transition-colors whitespace-nowrap"
            title={onNavigateToOrder ? `跳转到来源订单 ${sourceOrder.orderNumber ?? ''}` : '来源订单关联'}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-link-m text-xs" /></div>
            <span>来源：{sourceOrder.orderNumber ?? sourceOrder.id.slice(0, 8).toUpperCase()}</span>
            {sourceStaffRole && <span className="text-[10px] bg-indigo-100 text-indigo-500 px-1.5 py-0.5 rounded-full">{sourceStaffRole}</span>}
            <i className="ri-arrow-right-s-line text-xs" />
          </button>
        )}
      </div>
    </>
  );
}